import React, { useState } from "react";
import PublicNavbar from "../../components/public/PublicNavbar";
import { FaEnvelope, FaWhatsapp, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import { api } from "../../services/api";
import { toast } from "react-hot-toast";
import { buildWhatsAppUrl } from "../../utils/whatsapp";

const Contact = () => {
    const [formData, setFormData] = useState({
        customerName: "",
        email: "",
        phone: "",
        companyName: "",
        city: "",
        message: "",
        preferredContactMethod: "whatsapp"
    });
    const [submitting, setSubmitting] = useState(false);
    const [submittedRef, setSubmittedRef] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return; // Prevent double click
        setSubmitting(true);
        try {
            const payload = {
                customerName: formData.customerName,
                email: formData.email,
                phone: formData.phone,
                companyName: formData.companyName,
                city: formData.city,
                requirements: formData.message,
                customerRequirements: formData.message,
                preferredContact: formData.preferredContactMethod,
                productName: "General Commercial Gobo Enquiry",
                modelNumber: "GENERAL-ENQUIRY",
                quantity: 1
            };

            const { data } = await api.post("/api/enquiries", payload);
            setSubmittedRef(data.enquiryRef || data.data?.referenceNumber || "LMG-SUBMITTED");
            toast.success("Enquiry received! Our team will contact you shortly.");
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to submit enquiry. Please use WhatsApp or email directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans">
            <PublicNavbar />

            {/* Header Banner */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-14 px-6 text-center">
                <div className="max-w-4xl mx-auto flex flex-col items-center">
                    <img
                        src="/logo.png"
                        alt="LUMIGO Projection Solutions"
                        className="h-12 sm:h-14 w-auto object-contain mb-4 drop-shadow-xs"
                    />
                    <span className="text-[#E3B629] text-xs font-black tracking-[0.25em] uppercase block mb-2">
                        Get In Touch
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-[#263746] leading-tight">
                        Contact LUMIGO
                    </h1>
                    <p className="text-[#687482] text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                        Have questions about focal throw distances, custom dichroic glass gobos, or commercial pricing? Our lighting team is here to assist.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Contact Information & Channels */}
                    <div className="lg:col-span-5 space-y-8">
                        <div>
                            <span className="text-[#E3B629] text-xs font-black uppercase tracking-wider block mb-1">
                                Communication Channels
                            </span>
                            <h2 className="text-2xl font-bold text-[#263746]">Direct Business Desk</h2>
                            <p className="text-[#687482] text-xs mt-2 leading-relaxed">
                                We operate via responsive email and WhatsApp channels for prompt technical calculations and commercial quotes.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#F7F5EF] border border-[#E5E7EB] hover:border-[#E3B629] transition group shadow-sm">
                                <div className="w-12 h-12 rounded-xl bg-[#FFFFFF] text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                                    <FaEnvelope />
                                </div>
                                <div>
                                    <div className="text-[10px] text-[#687482] uppercase font-bold tracking-wider">Official Emails</div>
                                    <div className="text-sm font-bold text-[#263746]">
                                        <a href="mailto:fourlifters@gmail.com" className="hover:underline">fourlifters@gmail.com</a>
                                        <span className="text-[#687482] mx-1.5">|</span>
                                        <a href="mailto:sales@fourlifters.in" className="hover:underline">sales@fourlifters.in</a>
                                    </div>
                                    <div className="text-xs text-[#687482] mt-0.5">Direct response from our engineering and sales desk</div>
                                </div>
                            </div>

                            <a
                                href="https://wa.me/918087680893"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F7F5EF] border border-[#E5E7EB] hover:border-[#168B53] transition group shadow-sm"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#FFFFFF] text-[#168B53] border border-[#E5E7EB] flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                                    <FaWhatsapp />
                                </div>
                                <div>
                                    <div className="text-[10px] text-[#687482] uppercase font-bold tracking-wider">Mobile & Business WhatsApp</div>
                                    <div className="text-sm font-bold text-[#263746] group-hover:text-[#168B53] transition">+91 8087680893</div>
                                    <div className="text-xs text-[#687482] mt-0.5">Quick technical consultations, vector artwork reviews & quotes</div>
                                </div>
                            </a>

                            <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#F7F5EF] border border-[#E5E7EB] shadow-sm">
                                <div className="w-12 h-12 rounded-xl bg-[#FFFFFF] text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-xl">
                                    <FaMapMarkerAlt />
                                </div>
                                <div className="space-y-1">
                                    <div className="text-[10px] text-[#687482] uppercase font-bold tracking-wider">Registered Office & Works</div>
                                    <div className="text-sm font-bold text-[#263746]">Fourlifters Private Limited</div>
                                    <div className="text-xs text-[#687482] leading-relaxed">
                                        E-21/22, Kaygee Engineering Premises, T Block, MIDC Bhosari, Pune, Maharashtra, India - 411026
                                    </div>
                                    <div className="text-xs text-[#263746] pt-1">
                                        <strong>GST NO.:</strong> <span className="font-mono font-bold">27AAECF6202G1ZX</span>
                                        <span className="text-[#687482] mx-2">•</span>
                                        <strong>Website:</strong> <a href="https://www.fourlifters.in" target="_blank" rel="noopener noreferrer" className="text-[#E3B629] font-bold hover:underline">www.fourlifters.in</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 rounded-2xl bg-[#F7F5EF] border border-[#E5E7EB] text-xs text-[#687482] space-y-2">
                            <div className="text-[#263746] font-bold uppercase tracking-wider text-[11px]">Commercial Policy & Quotes</div>
                            <p className="leading-relaxed">
                                LUMIGO operates strictly on verified commercial invoicing. All equipment and custom gobos are dispatched from our Pune facility with insured logistics across India.
                            </p>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-7">
                        <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 shadow-sm">
                            {submittedRef ? (
                                <div className="text-center py-12 space-y-4">
                                    <div className="w-16 h-16 rounded-full bg-emerald-50 text-[#168B53] border border-emerald-200 flex items-center justify-center text-3xl mx-auto">
                                        <FaCheckCircle />
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#263746]">Enquiry Received!</h3>
                                    <p className="text-xs text-[#687482] max-w-md mx-auto">
                                        Your enquiry has been logged with Reference <strong className="text-[#263746] font-mono bg-[#F7F5EF] px-2 py-0.5 rounded border border-[#E5E7EB]">{submittedRef}</strong>. Our team has received an email notification.
                                    </p>
                                    <div className="pt-4 flex justify-center gap-4">
                                        <a
                                            href={buildWhatsAppUrl(`Hello LUMIGO,\n\nI just submitted an enquiry on your website.\nReference: ${submittedRef}\nName: ${formData.customerName}\nPhone: ${formData.phone}`)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-6 py-2.5 rounded-lg text-xs flex items-center gap-2 transition shadow-sm"
                                        >
                                            <FaWhatsapp /> Continue on WhatsApp
                                        </a>
                                        <button
                                            onClick={() => {
                                                setSubmittedRef(null);
                                                setFormData({ customerName: "", email: "", phone: "", companyName: "", city: "", message: "", preferredContactMethod: "whatsapp" });
                                            }}
                                            className="border border-[#E5E7EB] text-[#263746] hover:bg-[#F7F5EF] px-5 py-2.5 rounded-lg text-xs font-bold transition"
                                        >
                                            Submit Another
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <h3 className="text-xl font-bold text-[#263746] mb-2">Request Technical Quotation</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                                Your Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                name="customerName"
                                                value={formData.customerName}
                                                onChange={handleChange}
                                                placeholder="e.g. Rajesh Kumar"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                                Phone / WhatsApp Number *
                                            </label>
                                            <input
                                                type="tel"
                                                required
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="e.g. +91 9876543210"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="e.g. name@company.com"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                                Company / Organization (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                placeholder="e.g. Acme Corporation / Facilities Team"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                            City / Project Location
                                        </label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder="e.g. Mumbai, Pune, Bangalore"
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                            Project Details & Requirements *
                                        </label>
                                        <textarea
                                            required
                                            rows={4}
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Please describe throw distance, indoor vs outdoor mounting, custom logo artwork, or specific wattage..."
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-3 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                            Preferred Contact Method
                                        </label>
                                        <div className="flex gap-4 text-xs">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="preferredContactMethod"
                                                    value="whatsapp"
                                                    checked={formData.preferredContactMethod === "whatsapp"}
                                                    onChange={handleChange}
                                                    className="accent-[#E3B629]"
                                                />
                                                <span>WhatsApp Message</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="preferredContactMethod"
                                                    value="email"
                                                    checked={formData.preferredContactMethod === "email"}
                                                    onChange={handleChange}
                                                    className="accent-[#E3B629]"
                                                />
                                                <span>Email Response</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="preferredContactMethod"
                                                    value="phone"
                                                    checked={formData.preferredContactMethod === "phone"}
                                                    onChange={handleChange}
                                                    className="accent-[#E3B629]"
                                                />
                                                <span>Direct Phone Call</span>
                                            </label>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-4 shadow-sm"
                                    >
                                        <FaPaperPlane /> {submitting ? "Submitting..." : "Send Project Enquiry"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
